import {
  createApi,
  fetchBaseQuery,
  setupListeners,
} from "@reduxjs/toolkit/query/react";
import { configureStore } from "@reduxjs/toolkit";
import { createStandaloneToast } from "@chakra-ui/react";
const { toast } = createStandaloneToast();

import Reducers from "./Reducers";

const Config = window?.Config;

class Store {
  static BaseUrl = Config?.Server?.Domain + Config?.Server?.Path;
  
  // دالة لجلب الـ token من localStorage
  static getToken() {
    // 1. جرب token من localStorage
    const token = localStorage.getItem("token");
    if (token) {
      return token;
    }
    
    // 2. جرب authToken
    const authToken = localStorage.getItem("authToken");
    if (authToken) {
      return authToken;
    }
    
    // 3. جرب من User object في localStorage
    const userStr = localStorage.getItem("User");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user?.UserAccessToken) {
          return user.UserAccessToken;
        }
        if (user?.CustomerAccessToken) {
          return user.CustomerAccessToken;
        }
      } catch {
        // 
      }
    }
    
    return null;
  }
  
  // دالة لجلب اللغة
  static getLanguage() {
    // جرب من localStorage
    const lang = localStorage.getItem("language") || localStorage.getItem("lang");
    if (lang) {
      return lang;
    }
    
    // default
    return "en";
  }
  
  static RawBaseQuery = fetchBaseQuery({
    baseUrl: this.BaseUrl,
    cache: "no-store",
    credentials: "include", // اتركه include
    
    prepareHeaders: (headers) => {
      // 1. الحصول على الـ token
      const token = Store.getToken();
      
      // 2. الحصول على اللغة
      const lang = Store.getLanguage();
      
      // 3. إعداد الـ headers الأساسية
      headers.set("Content-Type", "application/json");
      headers.set("Accept", "application/json");
      headers.set("X-Requested-With", "true");
      headers.set("Local", lang);
      
      // 4. إضافة الـ Authorization header إذا كان token موجود
      if (token) {
        headers.set("X-T", `Bearer ${token}`);
      }
      return headers;
    },
  });
  
  static BaseQueryWithReauth = async (args, api, extraOptions) => {
    let result = await this.RawBaseQuery(args, api, extraOptions);
    // إذا كان 401
    if (result?.error?.status === 401) {      
      // مسح localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("authToken");
      localStorage.removeItem("User");
      // Dispatch logout
      Store.Dispatch("Auth/Logout");
      
      // Show toast
      toast({
        title: "Session Expired",
        description: "Please login again",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      
      // Redirect بعد 2 ثانية
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    }
    
    return result;
  };
  
  static ApiSlice = createApi({
    reducerPath: "CrmConfiguration",
    baseQuery: this.BaseQueryWithReauth,
    tagTypes: [
      "Branch",
      "Classification",
      "ContactWay",
      "Interest",
      "Nitch",
      "Package",
      "Platform",
      "Position",
      "ProductType",
      "Role",
      "SubSection",
      "SubSectionSub",
      "Team",
      "Analyze",
      "AuditLog",
      "Auth",
      "Message",
      "Room",
      "Contract",
      "Customer",
      "Subscription",
      "Task",
      "Meet",
      "Call",
      "Service",
      "ReportMasterConfig",
      "CustomerReports",
      "AccessManager",
      "Directory",
      "File",
      "Folder",
      "Notification",
      "User",
      "SpaceDocs",
      "SpaceNote",
    ],
    // eslint-disable-next-line no-unused-vars
    endpoints: (builder) => ({}),
  });
  
  static Configuration = configureStore({
    reducer: {
      ...Reducers,
      [Store.ApiSlice.reducerPath]: Store.ApiSlice.reducer,
    },
    middleware: (getDefaultMiddleware) => {
      return getDefaultMiddleware({
        serializableCheck: false,
      }).concat(Store.ApiSlice.middleware);
    },
  });
  
  static Dispatch(Action, Payload) {
    Store.Configuration.dispatch({
      type: Action,
      payload: Payload || {},
    });
  }
  
  static async OutsideFetch(url, method = "GET", body = null) {
    const args = {
      url,
      method,
      body,
    };
    return await Store.RawBaseQuery(
      args,
      {
        getState: () => Store.Configuration.getState(),
        dispatch: Store.Configuration.dispatch,
      },
      {}
    );
  }
  
  static Api = {
    // [GET] -
    Root: "/",
    // [GET] -
    File: "/file",
    //  [GET] - [POST] - [PUT] - [DELETE] - [PATCH]
    Auth: "/auth",
    //  [GET] - [POST] - [PUT] - [DELETE] - [PATCH]
    User: "/user",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Role: "/role",
    //  [GET] - [POST] - [PUT] - [DELETE]
    SubSection: "/section",
    //  [GET] - [POST] - [PUT] - [DELETE]
    SubSectionSub: "/section/sub",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Classification: "/classification",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Package: "/package",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Status: "/status",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Customer: "/customer",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Meet: "/meet",
    //  [GET] - [POST] - [PUT] - [DELETE]
    MeetNote: "/meet/note",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Call: "/call",
    //  [GET] - [POST] - [PUT] - [DELETE]
    AccessManager: "/acc",
    //  [GET] - [PUT] - [DELETE]
    Subscription: "/subscription",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Contract: "/contract",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Task: "/task",
    //  [GET] - [PATCH]
    Service: "/service",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Nitch: "/nitch",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Interest: "/interest",
    //  [GET] - [POST] - [PUT] - [DELETE]
    ProductType: "/producttype",
    //  [GET] - [POST] - [PUT] - [DELETE]
    ContactWay: "/contactway",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Platform: "/platform",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Position: "/position",
    //  [GET] - [POST] - [PUT] - [DELETE]
    ReportMasterConfig: "/report/master-config",
    //  [GET] - [PUT]
    ReportClientConfig: "/report/client-config",
    //  [GET] - [POST] - [PUT] - [DELETE]
    AiModel: "/ai/model",
    //  [GET] - [POST] - [PUT] - [DELETE]
    AiModelKey: "/ai/model-key",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Branch: "/branch",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Team: "/team",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Room: "/room",
    //  [GET] - [POST] - [PUT] - [DELETE]
    Directory: "/dir",
    //  [GET] - [DELETE]
    Message: "/msg",
    //  [GET] - [POST] - [PUT] - [DELETE] - [PATCH]
    Folder: "/folder",
    //  [GET] - [POST] - [PUT] - [DELETE] - [PATCH]
    FolderFiles: "/folder/file",
    //  [GET] - [POST] - [PUT] - [DELETE]
    AuditLog: "/logs",
    // [POST]
    Analyze: "/analyze",
    // [GET]
    Notification: "/notify",
    // Public
    Space: {
      Docs: "/space/doc",
      Note: "/space/note",
    },
    Report: {
      Main: "/report",
      TeleSales: "/report/ts",
      Sales: "/report/sales",
      Accounts: "/report/accounts",
      AccountManager: "/report/account-manager",
      Technical: "/report/technical",
      Revenue: "/report/revenue",
    },
  };
}
setupListeners(Store.Configuration.dispatch);

export default Store;